/** @jsxRuntime classic */
/** @jsx React.createElement */
/** @jsxFrag React.Fragment */

/**
 * React bindings for @baba/i18n. Uses the *classic* JSX runtime so this
 * module compiles to React.createElement() calls that work under both
 * React (web app) and preact/compat (extension) without needing each
 * toolchain to resolve a 'jsx-runtime' entry point.
 *
 *   <LocaleProvider>    — at app root. Hydrates from storage + detection.
 *   useLocale()         — returns { locale, setLocale, t, meta, ready }
 *   <LocalePicker />    — small dropdown. Drop anywhere.
 */

import * as React from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { DEFAULT_LOCALE, type LocaleCode, isSupported, LOCALES, readyLocales, localeMeta } from './locales'
import { browserSignals, detectLocale } from './detect'
import { getCatalog, type StringCatalog } from './strings'

const STORAGE_KEY = 'baba.paywall.locale'

interface LocaleContextValue {
  locale: LocaleCode
  setLocale: (code: LocaleCode) => void
  t: StringCatalog
  meta: ReturnType<typeof localeMeta>
  /** Always true once the provider has mounted. Exposed for symmetry. */
  ready: boolean
}

const LocaleContext = createContext<LocaleContextValue | null>(null)

function readExplicit(): LocaleCode | null {
  try {
    const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null
    return isSupported(stored) ? (stored as LocaleCode) : null
  } catch {
    return null
  }
}

function writeExplicit(code: LocaleCode) {
  try {
    localStorage.setItem(STORAGE_KEY, code)
  } catch {}
}

export function LocaleProvider({
  children,
  geoipCountry,
}: {
  children: ReactNode
  /**
   * Optional — if you have server-side geoip (e.g. from Cloudflare's
   * `cf-ipcountry` header passed down via SSR or a /geo endpoint),
   * provide it here. Without it, detection falls back to navigator +
   * timezone.
   */
  geoipCountry?: string | null
}) {
  const [locale, setLocaleState] = useState<LocaleCode>(() => {
    return detectLocale({
      explicit: readExplicit(),
      geoipCountry: geoipCountry || null,
      ...browserSignals(),
    })
  })

  // Sync across tabs (and for the extension: across sidepanel + popup).
  useEffect(() => {
    if (typeof window === 'undefined') return
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY || !e.newValue) return
      if (isSupported(e.newValue)) setLocaleState(e.newValue as LocaleCode)
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  const setLocale = useCallback((code: LocaleCode) => {
    writeExplicit(code)
    setLocaleState(code)
  }, [])

  const meta = useMemo(() => localeMeta(locale), [locale])
  const t = useMemo(() => getCatalog(locale), [locale])

  const value = useMemo<LocaleContextValue>(
    () => ({ locale, setLocale, t, meta, ready: true }),
    [locale, setLocale, t, meta],
  )

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext)
  if (ctx) return ctx
  // Un-provided fallback: return a context that renders English and
  // silently no-ops set(). Means components can useLocale() even outside
  // a provider without crashing (useful during first-mount transitions).
  return {
    locale: DEFAULT_LOCALE,
    setLocale: () => {},
    t: getCatalog(DEFAULT_LOCALE),
    meta: localeMeta(DEFAULT_LOCALE),
    ready: false,
  }
}

/**
 * Drop-in language picker. Renders as a small button that opens a native
 * <select>. Design-neutral — consumer styles the wrapper.
 */
export function LocalePicker({
  className,
  label,
}: {
  className?: string
  label?: string
}) {
  const { locale, setLocale } = useLocale()
  const options = readyLocales()
  return (
    <label className={className}>
      {label ? <span className="sr-only">{label}</span> : null}
      <select
        value={locale}
        onChange={(e) => setLocale(e.target.value as LocaleCode)}
        className="appearance-none bg-transparent text-xs text-muted hover:text-app focus:text-app border border-app rounded-md px-2 py-1 cursor-pointer transition-colors"
        aria-label={label || 'Change language'}
        dir="ltr"
      >
        {options.map((o) => (
          <option key={o.code} value={o.code}>
            {o.native}
          </option>
        ))}
      </select>
    </label>
  )
}

export { LOCALES, readyLocales, localeMeta }
